/**
 * Iter 96 — fixed mobile bottom navigation for Vendor + Admin dashboards
 * (spec §3): Dashboard | Bookings | Calendar | Vehicles/Vendors | More.
 * "More" opens a bottom sheet with the secondary sections.
 */
import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import {
  LayoutDashboard, ListChecks, CalendarRange, Car, Store, MoreHorizontal, ChevronRight,
} from "lucide-react";

const MAIN = {
  vendor: [
    { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { key: "bookings",  label: "Bookings",  icon: ListChecks },
    { key: "calendar",  label: "Calendar",  icon: CalendarRange },
    { key: "cars",      label: "Vehicles",  icon: Car },
  ],
  admin: [
    { key: "operations", label: "Dashboard", icon: LayoutDashboard },
    { key: "bookings",   label: "Bookings",  icon: ListChecks },
    { key: "calendar",   label: "Calendar",  icon: CalendarRange },
    { key: "vendors",    label: "Vendors",   icon: Store },
  ],
};

const MORE = {
  vendor: [
    { key: "today-ops", label: "Today Operations" },
    { key: "agreements", label: "Agreements" },
    { key: "payouts", label: "Payouts" },
    { key: "billing", label: "Payments & Billing" },
    { key: "fleet-finance", label: "Fleet Finance / Reports" },
    { key: "rates", label: "Rates & Seasons" },
    { key: "staff", label: "Staff" },
    { key: "documents", label: "Documents" },
    { key: "activity", label: "Activity Log" },
    { key: "widget", label: "Booking Widget & API" },
    { key: "fiscalization", label: "Fiscalization" },
    { key: "profile", label: "Settings & Profile" },
  ],
  admin: [
    { key: "today-ops", label: "Today Operations" },
    { key: "agreements", label: "Agreements" },
    { key: "billing", label: "Billing & Commissions" },
    { key: "site", label: "Site Settings" },
    { key: "tools", label: "Tools & Reports" },
    { key: "account", label: "My Account & Users" },
  ],
};

export default function OpsBottomNav({ role = "vendor", active, onNavigate }) {
  const [moreOpen, setMoreOpen] = useState(false);
  const main = MAIN[role] || MAIN.vendor;
  const more = MORE[role] || MORE.vendor;
  const moreKeys = more.map((m) => m.key);

  return (
    <nav
      className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-border flex items-stretch pb-[env(safe-area-inset-bottom)]"
      data-testid="ops-bottom-nav"
    >
      {main.map((it) => {
        const Icon = it.icon;
        const isActive = active === it.key;
        return (
          <button
            key={it.key}
            type="button"
            onClick={() => { setMoreOpen(false); onNavigate?.(it.key); }}
            className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 text-[10px] font-semibold transition ${isActive ? "text-blue-600" : "text-neutral-500"}`}
            data-testid={`ops-nav-${it.key}`}
          >
            <Icon className="h-5 w-5" />
            {it.label}
          </button>
        );
      })}
      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetTrigger asChild>
          <button
            type="button"
            className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 text-[10px] font-semibold transition ${moreKeys.includes(active) ? "text-blue-600" : "text-neutral-500"}`}
            data-testid="ops-nav-more"
          >
            <MoreHorizontal className="h-5 w-5" />
            More
          </button>
        </SheetTrigger>
        <SheetContent side="bottom" className="rounded-t-3xl max-h-[70vh] overflow-y-auto" data-testid="ops-more-sheet">
          <SheetHeader className="mb-2">
            <SheetTitle className="text-left">More sections</SheetTitle>
          </SheetHeader>
          <div className="divide-y divide-border">
            {more.map((m) => (
              <button
                key={m.key}
                type="button"
                onClick={() => { setMoreOpen(false); onNavigate?.(m.key); }}
                className={`w-full flex items-center justify-between py-3.5 text-sm font-semibold ${active === m.key ? "text-blue-600" : "text-neutral-800"}`}
                data-testid={`ops-more-${m.key}`}
              >
                {m.label}
                <ChevronRight className="h-4 w-4 text-neutral-400" />
              </button>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </nav>
  );
}
